import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { AudioBookCustomIcon, AudioBookPresetIcon } from "../../../icons";
import { VoiceClonerData, VoiceType } from "../VoiceClonerModal";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  onNext: (data: Partial<VoiceClonerData>) => void;
};

export const TypeSelector: FC<Props> = ({ open, setOpen, onNext }) => {
  const { t } = useTranslation();

  const types: VoiceType[] = ["Preset", "Custom"];

  const VoiceTypeFormSchema = Yup.object().shape({
    voiceType: Yup.string()
      .oneOf(types, t("errors.required"))
      .required(t("errors.required")),
  });

  const defaultValues: VoiceClonerData = { voiceType: "Preset" };

  const { handleSubmit, reset, watch, setValue } = useForm<VoiceClonerData>({
    defaultValues: defaultValues,
    resolver: yupResolver(VoiceTypeFormSchema),
  });

  const selectedVoiceType = watch("voiceType");

  const onSubmit = (data: VoiceClonerData) => {
    onNext({ voiceType: data.voiceType });
    reset();
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-4 pb-4 pt-5 text-left w-full max-w-sm sm:w-full sm:max-w-lg shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <DialogTitle
                    as="h3"
                    className="text-2xl font-semibold text-slate-800"
                  >
                    {t("audioBook.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row mt-5 space-y-4 sm:space-y-0 sm:space-x-3 justify-around mx-auto">
                <div className="flex flex-col items-center">
                  <div
                    className={[
                      selectedVoiceType === "Preset"
                        ? "border-herb-700"
                        : "border-gray-200",
                      "w-fit p-3 sm:p-4 border-2 rounded h-fit cursor-pointer",
                    ].join(" ")}
                  >
                    <AudioBookPresetIcon
                      onClick={() => setValue("voiceType", "Preset")}
                    />
                  </div>
                  <p className="text-center text-xs sm:text-sm mt-2 w-2/3">
                    {t("audioBook.presetVoice")}
                  </p>
                </div>
                <div className="flex flex-col items-center">
                  <div
                    className={[
                      selectedVoiceType === "Custom"
                        ? "border-herb-700"
                        : "border-gray-200",
                      "w-fit p-3 sm:p-4 border-2 rounded h-fit cursor-pointer",
                    ].join(" ")}
                  >
                    <AudioBookCustomIcon
                      onClick={() => setValue("voiceType", "Custom")}
                    />
                  </div>
                  <p className="text-center text-xs sm:text-sm mt-2 w-2/3">
                    {t("audioBook.customVoice")}
                  </p>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 flex justify-center sm:mx-auto">
                <button
                  type="submit"
                  className="w-full sm:w-auto inline-flex justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100"
                >
                  {t("next")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
